// Shows only the fields that match the chosen service; disabled fieldsets are excluded from the submitted request.
// It also totals the selected options from the price list so guests see their price before requesting.
class SBookingForm extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;
    const params = new URLSearchParams(window.location.search);
    const requested = params.get('service');
    if (requested) {
      const match = this.querySelector(`input[data-service="${CSS.escape(requested)}"]`);
      if (match) match.checked = true;
    }
    this.addEventListener(
      'change',
      (event) => {
        if (event.target instanceof HTMLInputElement && event.target.dataset.service) this.#sync();
        this.#estimate();
      },
      { signal }
    );
    this.addEventListener('input', () => this.#estimate(), { signal });
    this.#sync();
    this.#estimate();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #sync() {
    const checked = this.querySelector('input[data-service]:checked');
    const service = checked ? checked.dataset.service : '';
    for (const fieldset of this.querySelectorAll('fieldset[data-services]')) {
      const services = fieldset.dataset.services.split(' ');
      fieldset.disabled = !services.includes(service);
    }
  }

  #format(euros) {
    const currency = this.dataset.currency || 'EUR';
    const whole = Number.isInteger(euros);
    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(euros);
  }

  #count(role) {
    const field = this.querySelector(`[data-price="${role}"]`);
    const value = field ? parseInt(field.value, 10) : 0;
    return Number.isFinite(value) && value > 0 ? value : 0;
  }

  #lines() {
    const checked = this.querySelector('input[data-service]:checked');
    const service = checked ? checked.dataset.service : '';
    const adultLabel = this.dataset.labelAdult || '';
    const childLabel = this.dataset.labelChild || '';
    const lines = [];
    let partial = false;

    if (service === 'private') {
      const people = this.querySelector('[data-price="private-people"]');
      const hours = this.querySelector('[data-price="private-hours"]');
      const rows = (this.dataset.privatePrices || '').split('\n').map((row) => row.split('|').map((cell) => cell.trim()));
      const row = rows.find((cells) => cells[0] === people?.value);
      const price = row ? parseFloat(row[(hours?.selectedIndex ?? 0) + 1]) : NaN;
      if (Number.isFinite(price)) lines.push({ label: this.dataset.labelPerParty || '', amount: price });
    }

    if (service === 'group' || service === 'package') {
      const fieldset = this.querySelector(`fieldset[data-services="${service}"]`);
      const option = fieldset?.querySelector('[data-price="option"]')?.selectedOptions[0];
      const adults = this.#count('adults');
      const children = this.#count('children');
      if (option) {
        const adultPrice = parseInt(option.dataset.adult, 10) / 100;
        const childPrice = parseInt(option.dataset.child, 10) / 100;
        if (adults) lines.push({ label: `${adultLabel} × ${adults}`, amount: adults * adultPrice });
        if (children) lines.push({ label: `${childLabel} × ${children}`, amount: children * childPrice });
      }
    }

    if (service === 'rental') {
      const dayIndex = this.querySelector('[data-price="rental-days"]')?.selectedIndex ?? 0;
      const adults = this.#count('adults');
      const children = this.#count('children');
      for (const item of this.querySelectorAll('[data-price="item"]:checked')) {
        const adultPrice = parseFloat((item.dataset.adultPrices || '').split(',')[dayIndex]);
        const childPrice = parseFloat((item.dataset.childPrices || '').split(',')[dayIndex]);
        if (adults && Number.isFinite(adultPrice)) {
          lines.push({ label: `${item.dataset.name} – ${adultLabel} × ${adults}`, amount: adults * adultPrice });
        }
        if (children) {
          if (Number.isFinite(childPrice)) {
            lines.push({ label: `${item.dataset.name} – ${childLabel} × ${children}`, amount: children * childPrice });
          } else {
            partial = true;
          }
        }
      }
    }

    return { lines, partial };
  }

  #estimate() {
    const totalEl = this.querySelector('[data-estimate-total]');
    const emptyEl = this.querySelector('[data-estimate-empty]');
    const listEl = this.querySelector('[data-estimate-lines]');
    const partialEl = this.querySelector('[data-estimate-partial]');
    const input = this.querySelector('[data-estimate-input]');
    if (!totalEl || !listEl) return;

    const { lines, partial } = this.#lines();
    const total = lines.reduce((sum, line) => sum + line.amount, 0);
    const hasTotal = lines.length > 0;

    totalEl.hidden = !hasTotal;
    totalEl.textContent = hasTotal ? this.#format(total) : '';
    if (emptyEl) emptyEl.hidden = hasTotal;
    if (partialEl) partialEl.hidden = !partial;

    listEl.replaceChildren(
      ...lines.map((line) => {
        const li = document.createElement('li');
        li.className = 'flex justify-between gap-4';
        const label = document.createElement('span');
        label.textContent = line.label;
        const amount = document.createElement('bdi');
        amount.textContent = this.#format(line.amount);
        li.append(label, amount);
        return li;
      })
    );

    if (input) input.value = hasTotal ? this.#format(total) : '';
  }
}

if (!customElements.get('s-booking-form')) customElements.define('s-booking-form', SBookingForm);
